export const DOCUMENT_UPLOAD_LIMITS = Object.freeze({
  maximumFiles: 5,
  maximumFileBytes: 10 * 1024 * 1024,
  maximumTotalBytes: 25 * 1024 * 1024,
});

const typeByExtension = Object.freeze({
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
});

const genericMimeTypes = new Set(["", "application/octet-stream"]);

function extensionOf(name) {
  const match = String(name || "").toLowerCase().match(/\.([a-z0-9]+)$/);
  return match?.[1] || "";
}

export function sanitizeFileName(name) {
  const original = String(name || "document").split(/[\\/]/).pop() || "document";
  const extension = extensionOf(original);
  let base = extension ? original.slice(0, -(extension.length + 1)) : original;
  base = base
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^[-_.]+|[-_.]+$/g, "")
    .slice(0, 90);

  const safeBase = base || "document";
  return extension ? `${safeBase}.${extension}` : safeBase;
}

export function validateDocumentFiles(files) {
  const documents = Array.from(files || []);
  if (!documents.length) throw new Error("Zgjidhni të paktën një dokument.");
  if (documents.length > DOCUMENT_UPLOAD_LIMITS.maximumFiles) {
    throw new Error(`Mund të ngarkoni deri në ${DOCUMENT_UPLOAD_LIMITS.maximumFiles} dokumente.`);
  }

  let totalBytes = 0;
  const validated = documents.map((file) => {
    const extension = extensionOf(file?.name);
    const contentType = typeByExtension[extension];
    if (!contentType) {
      throw new Error(`Formati i dokumentit “${file?.name || "pa emër"}” nuk lejohet.`);
    }

    const size = Number(file?.size || 0);
    if (size <= 0) throw new Error(`Dokumenti “${file.name}” është bosh.`);
    if (size > DOCUMENT_UPLOAD_LIMITS.maximumFileBytes) {
      throw new Error(`Dokumenti “${file.name}” tejkalon kufirin 10 MB.`);
    }

    const declaredType = String(file?.type || "").toLowerCase();
    const compatibleHeicType = ["heic", "heif"].includes(extension) && ["image/heic", "image/heif"].includes(declaredType);
    if (!genericMimeTypes.has(declaredType) && declaredType !== contentType && !compatibleHeicType) {
      throw new Error(`Lloji i dokumentit “${file.name}” nuk përputhet me prapashtesën e tij.`);
    }

    totalBytes += size;
    return { file, safeName: sanitizeFileName(file.name), extension, contentType };
  });

  if (totalBytes > DOCUMENT_UPLOAD_LIMITS.maximumTotalBytes) {
    throw new Error("Madhësia totale e dokumenteve nuk mund të kalojë 25 MB.");
  }

  return validated;
}

function startsWith(bytes, expected, offset = 0) {
  return expected.every((value, index) => bytes[offset + index] === value);
}

function containsAscii(bytes, value) {
  const expected = new TextEncoder().encode(value);
  outer: for (let index = 0; index <= bytes.length - expected.length; index += 1) {
    for (let inner = 0; inner < expected.length; inner += 1) {
      if (bytes[index + inner] !== expected[inner]) continue outer;
    }
    return true;
  }
  return false;
}

export async function hasValidDocumentSignature(document) {
  const bytes = new Uint8Array(await document.file.arrayBuffer());

  if (document.extension === "pdf") {
    return containsAscii(bytes.slice(0, 1024), "%PDF-");
  }
  if (["jpg", "jpeg"].includes(document.extension)) {
    return startsWith(bytes, [0xff, 0xd8, 0xff]);
  }
  if (document.extension === "png") {
    return startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);
  }
  if (document.extension === "webp") {
    return startsWith(bytes, [0x52, 0x49, 0x46, 0x46]) && startsWith(bytes, [0x57, 0x45, 0x42, 0x50], 8);
  }
  if (["heic", "heif"].includes(document.extension)) {
    if (!startsWith(bytes, [0x66, 0x74, 0x79, 0x70], 4)) return false;
    const brand = new TextDecoder().decode(bytes.slice(8, 12));
    return new Set(["heic", "heix", "hevc", "hevx", "heim", "heis", "mif1", "msf1"]).has(brand);
  }
  if (document.extension === "docx") {
    return startsWith(bytes, [0x50, 0x4b])
      && containsAscii(bytes, "[Content_Types].xml")
      && containsAscii(bytes, "word/");
  }

  return false;
}
